import { Grid, Typography, Box, LinearProgress, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, useMediaQuery, useTheme } from "@mui/material";
import "../Common/css/styles.css";
import man from "../Assets/Images/man2.png"
import woman from "../Assets/Images/woman.png"
import { useState } from "react";
import PrimaryInputField from "src/Components/InputFields";
import CustomAlert from "src/Components/Alerts";
import Spinner from "src/Components/Spinner";
import { resetInsPassword, resetStudentPassword } from "src/Services/user_api";

export default function InstructorProfile() {
    const [gender, setGender] = useState(sessionStorage.getItem("gender"));
    const [name, setName] = useState(sessionStorage.getItem("username"));
    const subjectsRaw = JSON.parse(sessionStorage.getItem("instructorSubjects") || "[]");
    const freeCourses = JSON.parse(sessionStorage.getItem("freeCourses") || "[]");
    
    // group subjects by programme+stream for display (instructor only)
    const groupedSubjects = (() => {
        if (!Array.isArray(subjectsRaw)) return [];
        const map: Record<string, { programme: string; stream: string; subjects: string[] }> = {};
        for (const item of subjectsRaw) {
            const programme = item?.programme || "Unknown Programme";
            const stream = item?.stream || "";
            const key = `${programme}||${stream}`;
            if (!map[key]) map[key] = { programme, stream, subjects: [] };
            const subj = typeof item.subject === "string" ? item.subject : (item.subject?.name || item.subject || "");
            if (subj && !map[key].subjects.includes(subj)) map[key].subjects.push(subj);
        }
        return Object.values(map);
    })();

    const totalAmount = sessionStorage.getItem("totalAmount") || "0";
    const dueAmount = sessionStorage.getItem("dueAmount") || "0";
    const dueInstallments = sessionStorage.getItem("dueInstallments") || "0";
    const expireDate = sessionStorage.getItem("expireDate") || "N/A";
    const [isLoading, setIsLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertDescription, setAlertDescription] = useState("");
    const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");

    // numeric calculations for progress
    const total = parseFloat(totalAmount) || 0;
    const due = parseFloat(dueAmount) || 0;
    const paid = Math.max(0, total - due);
    const progress = total > 0 ? Math.round((paid / total) * 100) : 0;
    // change password dialog state
    const [changeOpen, setChangeOpen] = useState(false);
    const [cpEmail, setCpEmail] = useState(sessionStorage.getItem("email") || "");
    const [cpNew, setCpNew] = useState("");
    const [cpConfirm, setCpConfirm] = useState("");
    const [cpErrors, setCpErrors] = useState<{ email?: string; new?: string; confirm?: string }>({});
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const showAlert = (description: string, type: "success" | "error" | "warning") => {
        setAlertDescription(description);
        setAlertType(type);
        setAlertOpen(true);
    };

    const handleChangePassword = async () => {
        if (cpEmail.trim() === "" || cpNew.trim() === "" || cpConfirm.trim() === "") {
            showAlert("Please fill in all fields", "warning");
            return;
        }
        if (cpNew !== cpConfirm) {
            showAlert("New password and confirm password do not match", "warning");
            return;
        }

        setChangeOpen(false);
        setCpEmail("");
        setCpNew("");
        setCpConfirm("");

        setIsLoading(true);
        try {
            await resetInsPassword(sessionStorage.getItem("token") || "", cpEmail, cpNew);
            setIsLoading(false);
            showAlert("Password changed successfully", "success");

            setCpErrors({});
        } catch (error: any) {
            setIsLoading(false);
            showAlert(error.response.data.message, "error");
        }
    }

    return (
        <div className="comman-outer">
            <CustomAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                description={alertDescription}
                type={alertType}
                timeout={3000}
            />
            <Spinner isLoading={isLoading} />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className="profile-outer">
                        <div className="profile-banner1">
                            <img className="profile-image" src={man} alt="" />
                        </div>
                        <h2 className="profile-name">{name}</h2>

                        {/* instructor: programmes + streams + subjects (flex layout) */}
                        {groupedSubjects.length > 0 && (
                            <div className="pf-profile-meta" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div className="pf-section-title" style={{ color: '#000', fontWeight: 700 }}>Programmes & Subjects</div>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    {groupedSubjects.map((g, i) => (
                                        <div key={i} className="pf-programme-block" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                            <div className="pf-programme-title">{g.programme} <span className="pf-programme-stream"> {g.stream ? `— ${g.stream}` : ''}</span></div>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                                {g.subjects.map((s, si) => (
                                                    <span key={si} className="pf-subject-badge">{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Divider style={{ margin: "12px 0" }} />

                        {/* Change password button */}
                        <Button className="pf-change-password-button" onClick={() => setChangeOpen(true)}>Change Password</Button>

                        {/* Change Password Dialog */}
                        <Dialog
                            open={changeOpen}
                            onClose={() => setChangeOpen(false)}
                            fullScreen={fullScreen}
                            aria-labelledby="change-password-title"
                            PaperProps={{ sx: { bgcolor: "#0d0d0d", color: "#fff", borderRadius: 2 } }}
                        >
                            <DialogTitle id="change-password-title">Change Password</DialogTitle>
                            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, overflowX: "hidden", color: "black" }}>
                                <PrimaryInputField
                                    placeholder="Enter your email"
                                    value={cpEmail}
                                    type="text"
                                    disabled={false}
                                    width="97%"
                                    size="small"
                                    onChange={(e: any) => setCpEmail(e.target.value)}
                                />
                                <PrimaryInputField
                                    placeholder="New Password"
                                    value={cpNew}
                                    onChange={(e) => setCpNew(e.target.value)}
                                    type="password"
                                    width="97%"
                                    size="small"
                                />
                                <PrimaryInputField
                                    placeholder="Confirm Password"
                                    value={cpConfirm}
                                    onChange={(e) => setCpConfirm(e.target.value)}
                                    type="password"
                                    width="97%"
                                    size="small"
                                />
                            </DialogContent>
                            <DialogActions sx={{ px: 3, py: 2 }}>
                                <Button onClick={() => setChangeOpen(false)} variant="outlined">Cancel</Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        handleChangePassword();
                                    }}
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </div>
                </Grid>
            </Grid>
        </div>
    )
}